import React from 'react';
import { motion } from 'framer-motion';
import { Lock, Server, KeyRound, ShieldCheck, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const ITEMS = [
  {
    icon: Server,
    title: 'GCC data residency',
    body: 'Your data stays in-region. Tenants can be hosted in UAE, KSA or Qatar — never leaves your jurisdiction.',
  },
  {
    icon: Lock,
    title: 'Encrypted, end-to-end',
    body: 'AES-256 at rest, TLS 1.3 in transit. Field-level encryption for the most sensitive trade data.',
  },
  {
    icon: KeyRound,
    title: 'Your data is yours',
    body: 'No training on customer trades. Models are isolated per tenant. Delete on request — verifiable, irreversible.',
  },
  {
    icon: ShieldCheck,
    title: 'SOC 2 in progress',
    body: 'Type I targeted for Q4 2026. SSO, SAML, granular RBAC, full audit logs — available from day one. Aligned with KSA PDPL, UAE PDPL and Qatar PDPPL.',
  },
];

const SecurityStrip = () => {
  return (
    <section className="relative py-28 md:py-32 bg-canvas-dark overflow-hidden">
      <div className="absolute inset-0 bg-radial-amber opacity-50 pointer-events-none" />
      <div className="container-prose relative">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.5 }}
              className="text-[11px] font-semibold tracking-[0.12em] uppercase text-amber-light mb-5"
            >
              Security
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="text-display font-bold text-canvas-white text-balance"
            >
              Designed for the desks that can't afford to get this wrong.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 text-[15px] leading-relaxed text-ink-faint max-w-md"
            >
              Trade confirmations are some of the most sensitive data on the desk.
              We've built Core Trade so it can sit inside the security perimeter your
              compliance team already trusts.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-8"
            >
              <Link
                to="/security"
                className="group inline-flex items-center gap-1.5 text-[14px] font-medium text-amber-light hover:text-amber transition-colors"
              >
                Read the security overview
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </motion.div>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {ITEMS.map(({ icon: Icon, title, body }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="p-6 rounded-2xl border border-white/[0.07] bg-canvas-surface/50 backdrop-blur"
              >
                <Icon size={17} className="text-amber mb-4" strokeWidth={1.6} />
                <h3 className="text-[15px] font-semibold text-canvas-white tracking-tight mb-2">
                  {title}
                </h3>
                <p className="text-[13px] leading-relaxed text-ink-faint">{body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SecurityStrip;
